import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Heart, ShoppingCart, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { guestCartCount } from "@/lib/guestCart";
import { goSmart } from "@/lib/aiSearch";
import AccountMenu from "@/components/AccountMenu";

const LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/services", label: "Services" },
  { to: "/deals", label: "Deals" },
  { to: "/finance", label: "Finance" },
  { to: "/sellers", label: "Sellers" },
  { to: "/help", label: "Help" },
];

// One consistent shopper header used across every public shopping surface.
export default function ShopHeader() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [cart, setCart] = useState(0);
  const [loc, setLoc] = useState(null);
  const [locOpen, setLocOpen] = useState(false);
  const [form, setForm] = useState({ state: "", district: "", pincode: "" });
  const popRef = useRef(null);

  useEffect(() => {
    const upd = () => {
      if (user) api.get("/marketplace/cart").then((r) => setCart(r.data.count ?? (r.data.items || []).reduce((n, i) => n + (i.qty || 0), 0))).catch(() => {});
      else setCart(guestCartCount());
    };
    upd();
    window.addEventListener("cart-updated", upd);
    return () => window.removeEventListener("cart-updated", upd);
  }, [user]);

  useEffect(() => {
    api.get("/marketplace/location").then((r) => {
      const l = r.data.location;
      if (l && (l.state || l.district || l.pincode)) { setLoc(l); setForm({ state: l.state || "", district: l.district || "", pincode: l.pincode || "" }); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (locOpen && popRef.current && !popRef.current.contains(e.target)) setLocOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [locOpen]);

  const submitSearch = (e) => { e?.preventDefault?.(); goSmart(q, nav); };
  const lookupPin = async (pin) => {
    if (!/^\d{6}$/.test(pin)) return;
    try { const r = await api.get(`/geo/pincode/${pin}`); if (r.data?.valid) setForm((f) => ({ ...f, pincode: pin, state: r.data.state || f.state, district: r.data.district || f.district })); } catch { /* keep manual */ }
  };
  const saveLoc = async () => {
    const l = { country: "IN", state: form.state.trim(), district: form.district.trim(), pincode: form.pincode.trim() };
    setLoc(l); setLocOpen(false);
    try { await api.post("/marketplace/location", l); } catch { /* guests: session only */ }
    window.dispatchEvent(new Event("location-updated"));
  };

  const locLabel = loc && (loc.district || loc.state || loc.pincode) ? (loc.district || loc.state || loc.pincode) : "Set location";

  // NOTE: this MUST be a function returning inline JSX, NOT a nested component. A nested component
  // (`const SearchBox = () => …` used as `<SearchBox/>`) is a new type on every render, so React
  // remounts the <input> on each keystroke and focus is lost after one character. Returning plain
  // JSX at the same position lets React reconcile the same input and keep focus while typing.
  const searchBox = (className = "", idSuffix = "") => (
    <form onSubmit={submitSearch} className={`relative ${className}`} data-testid={`shop-header-search-form${idSuffix}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid={`shop-header-search${idSuffix}`}
        placeholder="Search products, brands or ask Suffle AI…"
        className="w-full rounded-full border border-white/15 bg-white/10 py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/45 focus:border-white/40 focus:outline-none" />
    </form>
  );

  return (
    <header className="sticky top-0 z-30 text-white" style={{ backgroundColor: "#0b0a1f" }} data-testid="shop-header">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3">
        <Link to="/" data-testid="shop-header-logo" className="shrink-0"><Logo variant="sm" /></Link>
        <nav className="hidden shrink-0 items-center gap-5 text-sm font-medium text-white/80 lg:flex">
          {LINKS.map((l) => <Link key={l.to} to={l.to} className="hover:text-white" data-testid={`shop-nav-${l.label.toLowerCase()}`}>{l.label}</Link>)}
        </nav>
        {searchBox("hidden min-w-0 flex-1 sm:block")}

        <div className="flex shrink-0 items-center gap-1.5">
          {/* Deliver to */}
          <div className="relative" ref={popRef}>
            <button onClick={() => setLocOpen((o) => !o)} data-testid="shop-header-deliverto"
              className="hidden items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 text-xs text-white/90 hover:bg-white/20 sm:flex">
              <MapPin className="h-4 w-4 text-blue-300" />
              <span className="max-w-[120px] truncate"><span className="text-white/50">Deliver to </span><b>{locLabel}</b></span>
              <ChevronDown className="h-3.5 w-3.5 text-white/50" />
            </button>
            {locOpen && (
              <div className="absolute right-0 z-40 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-3 text-slate-700 shadow-xl" data-testid="shop-header-loc-pop">
                <p className="mb-2 text-xs font-semibold text-slate-500">Set your delivery location</p>
                <input value={form.pincode} onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setForm((f) => ({ ...f, pincode: v })); lookupPin(v); }} placeholder="Pincode (auto-fills state & district)" data-testid="shop-header-loc-pincode" className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
                <div className="mb-2 grid grid-cols-2 gap-2">
                  <input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} placeholder="State" data-testid="shop-header-loc-state" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
                  <input value={form.district} onChange={(e) => setForm((f) => ({ ...f, district: e.target.value }))} placeholder="District" data-testid="shop-header-loc-district" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
                </div>
                <button onClick={saveLoc} data-testid="shop-header-loc-save" className="w-full rounded-full brand-gradient-bg py-2 text-sm font-semibold text-white">Use this location</button>
              </div>
            )}
          </div>

          <button onClick={() => nav(user ? "/app/wishlist" : "/login")} data-testid="shop-header-wishlist" title="Wishlist" className="hidden rounded-full bg-white/10 p-2 hover:bg-white/20 sm:inline-flex">
            <Heart className="h-4 w-4" />
          </button>

          <Link to="/cart" data-testid="shop-header-cart" title="Cart" className="relative inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-2 text-xs font-medium hover:bg-white/20">
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {cart > 0 && <span data-testid="shop-header-cart-count" className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{cart}</span>}
          </Link>

          {user ? (
            <AccountMenu variant="dark" />
          ) : (
            <>
              <button onClick={() => nav("/login")} data-testid="shop-header-login" className="rounded-full border border-white/25 px-3 py-2 text-xs font-medium text-white/90 hover:bg-white/10">Log In</button>
              <button onClick={() => nav("/register")} data-testid="shop-header-register" className="rounded-full brand-gradient-bg px-3 py-2 text-xs font-semibold text-white">Sign Up</button>
            </>
          )}
        </div>
      </div>
      {/* Mobile search row */}
      <div className="px-4 pb-3 sm:hidden">
        {searchBox("", "-mobile")}
      </div>
    </header>
  );
}
